import { Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { OVERALL_ROW, compareRows, type CompareCell, type CompareRow, type RatingTrend } from './ratingApi'
import { useQuarterLabel } from './useQuarterLabel'

interface Props {
  trend: RatingTrend
  quarters: readonly string[]
}

function Delta({ value }: { value: number }) {
  if (value === 0) return <Typography.Text type="secondary">±0.00</Typography.Text>
  return (
    <Typography.Text type={value > 0 ? 'success' : 'danger'}>
      {`${value > 0 ? '▲ +' : '▼ '}${value.toFixed(2)}`}
    </Typography.Text>
  )
}

export function RatingCompareTable({ trend, quarters }: Props) {
  const { t } = useTranslation()
  const quarterLabel = useQuarterLabel()

  const columns: ColumnsType<CompareRow> = [
    {
      key: 'criterion',
      title: t('hr.rating.compareCriterion'),
      fixed: 'left',
      width: 200,
      render: (_value, row) =>
        row.key === OVERALL_ROW ? (
          <Typography.Text strong>{t('hr.rating.seriesOverall')}</Typography.Text>
        ) : (
          <Typography.Text type={row.active ? undefined : 'secondary'}>
            {row.active ? row.criterionName : `${row.criterionName ?? ''} · ${t('hr.rating.inactive')}`}
          </Typography.Text>
        ),
    },
    ...quarters.map((quarter, index) => ({
      key: quarter,
      title: quarterLabel(quarter),
      width: 120,
      align: 'right' as const,
      render: (_value: unknown, row: CompareRow) => {
        const cell: CompareCell | undefined = row.cells[index]
        if (cell === undefined || cell.average === null) {
          return <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        }
        return (
          <Space direction="vertical" size={0} align="end">
            <Typography.Text strong={row.key === OVERALL_ROW}>{cell.average.toFixed(2)}</Typography.Text>
            {cell.delta === null ? null : (
              <span style={{ fontSize: 12 }}>
                <Delta value={cell.delta} />
              </span>
            )}
          </Space>
        )
      },
    })),
  ]

  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t('hr.rating.compareHint')}
      </Typography.Paragraph>
      <Table<CompareRow>
        rowKey={(row) => row.key}
        columns={columns}
        dataSource={compareRows(trend, quarters)}
        size="small"
        pagination={false}
        scroll={{ x: 200 + quarters.length * 120 }}
      />
    </>
  )
}
